import { useMemo } from "react";
import { useBinanceSpotEthBtcQuote } from "@/hooks/api/useBinanceSpotEthBtcQuote";
import { useAifPriceFromBackend } from "@/hooks/api/useAifPriceFromBackend";
import { formatNumberTruncate } from "@/lib/utils";
import { getTokenName } from "@/config/common.ts";
import { CCC_AIF_ADDRESS, CCC_FUSD_ADDRESS } from "@/config/env.ts";

/**
 * 顶栏行情：
 * - BTC / ETH 走 `useBinanceSpotEthBtcQuote`（5s 刷新）
 * - TSK 走后端 `useAifPriceFromBackend`（30s 刷新）
 *   后端未响应或返回非法价时退化到 `TSK_FALLBACK_PRICE`，避免出现 "$0"
 */
export interface CoinPrice {
  symbol: string;
  price: number;
  change24h: number;
  prevPrice: number;
}

function pairToSymbol(pair: string): "BTC" | "ETH" | null {
  const p = pair.trim().replace(/-/g, "").toUpperCase();
  if (p === "BTCUSDT") return "BTC";
  if (p === "ETHUSDT") return "ETH";
  return null;
}

const ZERO_BTC: CoinPrice = {
  symbol: "BTC",
  price: 0,
  change24h: 0,
  prevPrice: 0,
};
const ZERO_ETH: CoinPrice = {
  symbol: "ETH",
  price: 0,
  change24h: 0,
  prevPrice: 0,
};

export function useCryptoPrices(): CoinPrice[] {
  const { data } = useBinanceSpotEthBtcQuote();
  const { data: aif } = useAifPriceFromBackend();

  const tsk = useMemo<CoinPrice>(() => {
    const price = +(aif?.currentPriceUsd ?? 0);
    const change24hRaw = +(aif?.priceChange24H ?? 0);

    return {
      symbol: getTokenName(CCC_AIF_ADDRESS),
      price,
      change24h: change24hRaw,
      prevPrice: price,
    };
  }, [aif]);
  const [btc, eth] = useMemo(() => {
    const items = data?.items;
    if (!items?.length) {
      return [ZERO_BTC, ZERO_ETH] as const;
    }

    let nextBtc = { ...ZERO_BTC };
    let nextEth = { ...ZERO_ETH };

    for (const row of items) {
      const sym = pairToSymbol(row.pair);
      if (!sym) continue;

      const price = parseFloat(row.lastPrice);
      const change24h = parseFloat(row.priceChangePercent24h);
      if (isNaN(price)) continue;

      if (sym === "BTC") {
        nextBtc = {
          symbol: "BTC",
          price,
          change24h: isNaN(change24h) ? 0 : change24h,
          prevPrice: price,
        };
      } else if (sym === "ETH") {
        nextEth = {
          symbol: "ETH",
          price,
          change24h: isNaN(change24h) ? 0 : change24h,
          prevPrice: price,
        };
      }
    }

    return [nextBtc, nextEth] as const;
  }, [data]);

  return [tsk, btc, eth];
}
